import { Badge, Button, EmptyState, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PutawayQueueItem } from '@warehouse/shared';
import { PackageOpen } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../../components/PageHeader';
import { formatQty, formatRelative } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { receivingApi, receivingKeys } from '../api';
import { ScanField } from '../components/ScanField';

/**
 * `/warehouse/putaway` — scan pallet → scan bin (spec §5.7).
 *
 * The queue on the right is the same work list the scanner shows; clicking a
 * pallet fills the pallet field, so the flow works with a wedge scanner, a
 * tablet finger, or both in the same minute.
 */
export function PutawayPage(): ReactNode {
  const { t } = useTranslation('receiving');
  const queryClient = useQueryClient();

  const [palletCode, setPalletCode] = useState('');
  const [binCode, setBinCode] = useState('');
  const [done, setDone] = useState(0);

  const queue = useQuery({
    queryKey: receivingKeys.putawayQueue(),
    queryFn: () => receivingApi.putawayQueue(),
  });
  useLiveQuery(['stock', 'pallets'], receivingKeys.putawayQueue());

  const putaway = useMutation({
    mutationFn: () => receivingApi.putaway({ palletCode, toBinCode: binCode }),
    onSuccess: async (result) => {
      toastSuccess(t('putaway.moved', { lpn: result.lpn, bin: result.toBinCode }));
      setPalletCode('');
      setBinCode('');
      setDone((n) => n + 1);
      await queryClient.invalidateQueries({ queryKey: receivingKeys.putawayQueue() });
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const ready = palletCode.trim().length > 0 && binCode.trim().length > 0;

  return (
    <>
      <PageHeader title={t('putaway.title')} subtitle={t('putaway.subtitle')} />

      <div className="grid gap-6 lg:grid-cols-[24rem_minmax(0,1fr)]">
        <Surface className="space-y-4 p-4">
          <ScanField
            label={t('putaway.scanPallet')}
            placeholder={t('putaway.scanPallet')}
            refocusKey={done}
            onScan={(code) => {
              setPalletCode(code);
            }}
            testId="putaway-pallet"
            submitLabel={t('putaway.selectPallet')}
          />
          {palletCode ? (
            <p className="font-mono text-sm text-ink" data-testid="putaway-pallet-value">
              {palletCode}
            </p>
          ) : (
            <p className="text-xs text-ink-muted">{t('putaway.palletRequired')}</p>
          )}

          <ScanField
            label={t('putaway.scanBin')}
            placeholder={t('putaway.scanBin')}
            autoFocus={false}
            onScan={(code) => {
              setBinCode(code);
            }}
            testId="putaway-bin"
            submitLabel={t('putaway.selectBin')}
          />
          {binCode ? (
            <p className="font-mono text-sm text-ink" data-testid="putaway-bin-value">
              {binCode}
            </p>
          ) : (
            <p className="text-xs text-ink-muted">{t('putaway.binRequired')}</p>
          )}

          <Button
            className="w-full"
            disabled={!ready}
            loading={putaway.isPending}
            onClick={() => {
              putaway.mutate();
            }}
          >
            {t('putaway.submit')}
          </Button>
        </Surface>

        <Surface className="p-4">
          <h3 className="mb-3 text-sm font-medium text-ink">{t('putaway.queue')}</h3>
          {(queue.data ?? []).length === 0 ? (
            <EmptyState bare icon={<PackageOpen />} title={t('putaway.empty')} />
          ) : (
            <ul className="divide-y divide-surface-border">
              {(queue.data ?? []).map((item) => (
                <QueueRow
                  key={item.palletId || `loose-${item.binId}`}
                  item={item}
                  onSelect={() => {
                    if (item.lpn) setPalletCode(item.lpn);
                  }}
                />
              ))}
            </ul>
          )}
        </Surface>
      </div>
    </>
  );
}

function QueueRow({ item, onSelect }: { item: PutawayQueueItem; onSelect: () => void }): ReactNode {
  const { t } = useTranslation('receiving');
  const loose = item.lpn.length === 0;

  return (
    <li className="py-3">
      <button
        type="button"
        className="w-full text-left disabled:cursor-default"
        onClick={onSelect}
        disabled={loose}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-mono text-ink">{loose ? t('putaway.loose') : item.lpn}</span>
          <span className="text-xs text-ink-muted">
            {item.binCode} · {formatQty(item.totalQtyBase)} ·{' '}
            {item.receivedAt ? formatRelative(item.receivedAt) : '—'}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {item.ssccAlias ? (
            <Badge tone="info" size="sm">
              SSCC {item.ssccAlias}
            </Badge>
          ) : null}
          {item.articles.map((article) => (
            <Badge key={article.articleId} tone="neutral" size="sm">
              {article.articleNo} · {formatQty(article.qtyBase)}
            </Badge>
          ))}
        </div>
      </button>
    </li>
  );
}
